import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AutoComplete,
  Button,
  Descriptions,
  Empty,
  Form,
  Input,
  InputNumber,
  message as antdMessage,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  TimePicker,
  Typography,
  Upload,
} from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { ColumnsType } from 'antd/es/table';
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  GiftOutlined,
  PlusOutlined,
  QrcodeOutlined,
  ReloadOutlined,
  SyncOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { giftService } from '../services/giftService';
import {
  AssignGiftPayload,
  Gift,
  GiftAssignment,
  GiftStoreAllocation,
  isGiftActive,
} from '../types/gift';
import { userService } from '../services/userService';
import { storeService } from '../services/storeService';
import type { User } from '../types/users';
import type { Store } from '../types/store';

const { Text } = Typography;

interface GiftFormValues {
  name: string;
  description?: string;
  storeQuantities?: Record<string, number | null>;
  resetEnabled?: boolean;
  resetTime?: Dayjs | null;
}

interface AssignFormValues {
  giftId: string;
  userId: string;
  storeId: string;
}

type GiftStatusFilter = 'all' | 'active' | 'inactive';

const USERS_CACHE_TTL_MS = 5 * 60 * 1000;
const DEFAULT_RESET_TIME = dayjs('17:00', 'HH:mm');

const formatDateTime = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
};

const formatVietnameseDateTime = (value?: string) => {
  if (!value) return '';
  const date = dayjs(value);
  return date.isValid() ? date.format('HH:mm DD/MM/YYYY') : '';
};

const GiftManagementSection: React.FC = () => {
  const [gifts, setGifts] = useState<Gift[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingStores, setLoadingStores] = useState(false);
  const [giftModalVisible, setGiftModalVisible] = useState(false);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [editingGift, setEditingGift] = useState<Gift | null>(null);
  const [assigningGift, setAssigningGift] = useState<Gift | null>(null);
  const [currentAssignment, setCurrentAssignment] = useState<GiftAssignment | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [assignedModalVisible, setAssignedModalVisible] = useState(false);
  const [assignedLoading, setAssignedLoading] = useState(false);
  const [assignedRows, setAssignedRows] = useState<Array<{
    assignmentId: string;
    userId: string;
    userName?: string;
    assignedAt?: string;
    status?: string;
    storeName?: string;
    source?: string;
  }>>([]);
  const [assignedGift, setAssignedGift] = useState<Gift | null>(null);
  const [deletingAssignmentId, setDeletingAssignmentId] = useState<string | null>(null);
  const [qrPreviewVisible, setQrPreviewVisible] = useState(false);
  const [qrPreviewData, setQrPreviewData] = useState<{
    qrCode?: string;
    qrTarget?: string;
    status?: string;
    storeName?: string;
  } | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [statusFilter, setStatusFilter] = useState<GiftStatusFilter>('all');
  const [toggleLoadingId, setToggleLoadingId] = useState<string | null>(null);
  const [resettingGiftId, setResettingGiftId] = useState<string | null>(null);
  const [deletingGiftId, setDeletingGiftId] = useState<string | null>(null);
  const [resettingAll, setResettingAll] = useState(false);

  const [giftForm] = Form.useForm<GiftFormValues>();
  const [assignForm] = Form.useForm<AssignFormValues>();

  const watchedStoreQuantities = Form.useWatch('storeQuantities', giftForm);
  const watchedResetEnabled = Form.useWatch('resetEnabled', giftForm);

  const isMountedRef = useRef(true);
  const giftsRequestRef = useRef<Promise<Gift[]> | null>(null);
  const storesRequestRef = useRef<Promise<Store[]> | null>(null);
  const usersRequestRef = useRef<Promise<User[]> | null>(null);
  const usersFetchedAtRef = useRef(0);
  const userNameCacheRef = useRef<Map<string, string>>(new Map());
  const userNameRequestRef = useRef<Map<string, Promise<string | undefined>>>(new Map());
  const assignedModalRequestIdRef = useRef(0);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const syncUserNameCache = useCallback((nextUsers: User[]) => {
    nextUsers.forEach((user) => {
      if (user.id) {
        userNameCacheRef.current.set(user.id, user.name || '');
      }
    });
  }, []);

  const fetchGifts = useCallback(async () => {
    if (giftsRequestRef.current) {
      return giftsRequestRef.current;
    }

    setLoading(true);
    const request = giftService.getGifts();
    giftsRequestRef.current = request;

    try {
      const data = await request;
      if (isMountedRef.current) {
        setGifts(data);
      }
      return data;
    } catch (error) {
      console.error('Error fetching gifts:', error);
      if (isMountedRef.current) {
        antdMessage.error('Không thể tải danh sách quà tặng');
      }
      return [];
    } finally {
      giftsRequestRef.current = null;
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const loadStores = useCallback(async () => {
    if (stores.length > 0) {
      return stores;
    }

    if (storesRequestRef.current) {
      return storesRequestRef.current;
    }

    setLoadingStores(true);
    const request = storeService.getActiveStores();
    storesRequestRef.current = request;

    try {
      const data = await request;
      if (isMountedRef.current) {
        setStores(data.filter((store) => !!store.id));
      }
      return data;
    } catch (error) {
      console.error('Error loading stores:', error);
      if (isMountedRef.current) {
        antdMessage.error('Không thể tải danh sách cơ sở');
      }
      return [];
    } finally {
      storesRequestRef.current = null;
      if (isMountedRef.current) {
        setLoadingStores(false);
      }
    }
  }, [stores]);

  useEffect(() => {
    fetchGifts().catch(() => undefined);
    loadStores().catch(() => undefined);
  }, [fetchGifts, loadStores]);

  const loadUsers = useCallback(async () => {
    const now = Date.now();
    const isCacheValid = users.length > 0 && now - usersFetchedAtRef.current < USERS_CACHE_TTL_MS;

    if (isCacheValid) {
      return users;
    }

    if (usersRequestRef.current) {
      return usersRequestRef.current;
    }

    setLoadingUsers(true);
    const request = userService.getAllUsers();
    usersRequestRef.current = request;

    try {
      const data = await request;
      if (isMountedRef.current) {
        setUsers(data);
        usersFetchedAtRef.current = Date.now();
        syncUserNameCache(data);
      }
      return data;
    } catch (error) {
      console.error('Error loading users:', error);
      throw error;
    } finally {
      usersRequestRef.current = null;
      if (isMountedRef.current) {
        setLoadingUsers(false);
      }
    }
  }, [syncUserNameCache, users]);

  const getUserNameById = useCallback(async (userId: string): Promise<string | undefined> => {
    if (!userId) return undefined;

    const cached = userNameCacheRef.current.get(userId);
    if (cached !== undefined) {
      return cached;
    }

    const existingRequest = userNameRequestRef.current.get(userId);
    if (existingRequest) {
      return existingRequest;
    }

    const request = userService
      .getUserById(userId)
      .then((user) => {
        const userName = (user as any)?.name || '';
        userNameCacheRef.current.set(userId, userName);
        return userName;
      })
      .catch(() => undefined)
      .finally(() => {
        userNameRequestRef.current.delete(userId);
      });

    userNameRequestRef.current.set(userId, request);
    return request;
  }, []);

  const editableStores = useMemo(() => {
    const storeMap = new Map<string, { id: string; name: string }>();

    stores.forEach((store) => {
      if (store.id) {
        storeMap.set(store.id, { id: store.id, name: store.name });
      }
    });

    editingGift?.storeAllocations?.forEach((allocation) => {
      if (allocation.storeId && !storeMap.has(allocation.storeId)) {
        storeMap.set(allocation.storeId, {
          id: allocation.storeId,
          name: allocation.storeName || allocation.storeId,
        });
      }
    });

    return Array.from(storeMap.values());
  }, [editingGift, stores]);

  const computedTotalQuantity = useMemo(
    () => Object.values(watchedStoreQuantities || {}).reduce<number>(
      (sum, value) => sum + Math.max(0, Number(value || 0)),
      0,
    ),
    [watchedStoreQuantities],
  );

  const assignableStoreAllocations = useMemo(
    () => (assigningGift?.storeAllocations || []).filter((allocation) => allocation.totalQuantity > 0),
    [assigningGift],
  );

  const assignableStoreOptions = useMemo(
    () => assignableStoreAllocations.map((allocation) => ({
      value: allocation.storeId,
      label: `${allocation.storeName} - còn đăng ký ${allocation.availableQuantity}/${allocation.totalQuantity}`,
      disabled: allocation.availableQuantity <= 0,
    })),
    [assignableStoreAllocations],
  );

  const userOptions = useMemo(
    () => users.map((user) => ({
      value: user.id || '',
      label: `${user.name || 'Khách hàng'}${user.phoneNumber ? ` - ${user.phoneNumber}` : ''} (${user.id})`,
    })),
    [users],
  );

  const giftStats = useMemo(() => {
    const active = gifts.filter((gift) => isGiftActive(gift)).length;
    return {
      total: gifts.length,
      active,
      inactive: gifts.length - active,
    };
  }, [gifts]);

  const filteredGifts = useMemo(() => {
    const filtered = statusFilter === 'active'
      ? gifts.filter((gift) => isGiftActive(gift))
      : statusFilter === 'inactive'
        ? gifts.filter((gift) => !isGiftActive(gift))
        : gifts;

    return [...filtered].sort((left, right) => {
      const leftActiveRank = isGiftActive(left) ? 0 : 1;
      const rightActiveRank = isGiftActive(right) ? 0 : 1;

      if (leftActiveRank !== rightActiveRank) {
        return leftActiveRank - rightActiveRank;
      }

      const leftCreatedAt = left.createdAt ? new Date(left.createdAt).getTime() : 0;
      const rightCreatedAt = right.createdAt ? new Date(right.createdAt).getTime() : 0;

      if (leftCreatedAt !== rightCreatedAt) {
        return rightCreatedAt - leftCreatedAt;
      }

      return left.name.localeCompare(right.name, 'vi');
    });
  }, [gifts, statusFilter]);

  const resetGiftModal = useCallback(() => {
    setGiftModalVisible(false);
    setEditingGift(null);
    setImageFile(null);
    setFileList([]);
    giftForm.resetFields();
  }, [giftForm]);

  const openCreateModal = useCallback(async () => {
    setEditingGift(null);
    setImageFile(null);
    setFileList([]);
    await loadStores();

    const defaultQuantities = (stores.length > 0 ? stores : await loadStores())
      .filter((store) => !!store.id)
      .reduce<Record<string, number>>((acc, store) => {
        acc[store.id!] = 0;
        return acc;
      }, {});

    giftForm.setFieldsValue({
      name: '',
      description: '',
      storeQuantities: defaultQuantities,
      resetEnabled: false,
      resetTime: DEFAULT_RESET_TIME,
    });
    setGiftModalVisible(true);
  }, [giftForm, loadStores, stores]);

  const openEditModal = useCallback(async (gift: Gift) => {
    setEditingGift(gift);
    setImageFile(null);
    const loadedStores = stores.length > 0 ? stores : await loadStores();
    const mergedStores = new Map<string, { id: string; name: string }>();

    loadedStores.filter((store) => !!store.id).forEach((store) => {
      mergedStores.set(store.id!, { id: store.id!, name: store.name });
    });

    gift.storeAllocations?.forEach((allocation) => {
      if (allocation.storeId && !mergedStores.has(allocation.storeId)) {
        mergedStores.set(allocation.storeId, {
          id: allocation.storeId,
          name: allocation.storeName || allocation.storeId,
        });
      }
    });

    const storeQuantities = Array.from(mergedStores.values())
      .reduce<Record<string, number>>((acc, store) => {
        const matchedAllocation = gift.storeAllocations?.find((allocation) => allocation.storeId === store.id);
        acc[store.id] = matchedAllocation?.totalQuantity ?? 0;
        return acc;
      }, {});

    giftForm.setFieldsValue({
      name: gift.name,
      description: gift.description,
      storeQuantities,
      resetEnabled: Boolean(gift.resetConfig?.enabled),
      resetTime: gift.resetConfig
        ? dayjs(
          `${String(gift.resetConfig.dailyResetHour).padStart(2, '0')}:${String(gift.resetConfig.dailyResetMinute).padStart(2, '0')}`,
          'HH:mm',
        )
        : DEFAULT_RESET_TIME,
    });

    if (gift.imageUrl) {
      setFileList([
        {
          uid: '-1',
          name: 'image.jpg',
          status: 'done',
          url: gift.imageUrl,
        },
      ]);
    } else {
      setFileList([]);
    }

    setGiftModalVisible(true);
  }, [giftForm, loadStores, stores]);

  const buildStoreAllocationsFromForm = useCallback((values: GiftFormValues, baseGift?: Gift | null): GiftStoreAllocation[] => {
    const quantityMap = values.storeQuantities || {};
    const storeNameMap = new Map<string, string>();

    editableStores.forEach((store) => {
      storeNameMap.set(store.id, store.name);
    });

    baseGift?.storeAllocations?.forEach((allocation) => {
      if (allocation.storeId && allocation.storeName) {
        storeNameMap.set(allocation.storeId, allocation.storeName);
      }
    });

    return Object.entries(quantityMap)
      .map(([storeId, rawQuantity]) => {
        const totalQuantity = Math.max(0, Number(rawQuantity || 0));
        const existingAllocation = baseGift?.storeAllocations?.find((allocation) => allocation.storeId === storeId);

        if (!existingAllocation) {
          return {
            storeId,
            storeName: storeNameMap.get(storeId) || storeId,
            totalQuantity,
            availableQuantity: totalQuantity,
            assignedCount: 0,
            usedQuantity: 0,
          };
        }

        const quantityDelta = totalQuantity - existingAllocation.totalQuantity;
        return {
          ...existingAllocation,
          storeName: existingAllocation.storeName || storeNameMap.get(storeId) || storeId,
          totalQuantity,
          availableQuantity: Math.max(0, existingAllocation.availableQuantity + quantityDelta),
        };
      })
      .filter((allocation) => allocation.totalQuantity > 0);
  }, [editableStores]);

  const handleGiftSubmit = useCallback(async () => {
    try {
      const values = await giftForm.validateFields();
      const storeAllocations = buildStoreAllocationsFromForm(values, editingGift);
      const resetTime = values.resetTime || DEFAULT_RESET_TIME;
      const resetConfig = {
        enabled: Boolean(values.resetEnabled),
        dailyResetHour: resetTime.hour(),
        dailyResetMinute: resetTime.minute(),
        ...(editingGift?.resetConfig?.lastResetAt ? { lastResetAt: editingGift.resetConfig.lastResetAt } : {}),
      };

      if (storeAllocations.length === 0) {
        antdMessage.error('Vui lòng phân bổ số lượng cho ít nhất 1 cơ sở');
        return;
      }

      setSubmitting(true);

      if (editingGift) {
        await giftService.updateGift(
          editingGift.id,
          {
            name: values.name,
            description: values.description,
            storeAllocations,
            resetConfig,
          },
          imageFile || undefined,
        );
        antdMessage.success('Cập nhật quà tặng thành công');
      } else {
        await giftService.createGift(
          {
            name: values.name,
            description: values.description,
            isActive: true,
            storeAllocations,
            resetConfig,
          },
          imageFile || undefined,
        );
        antdMessage.success('Tạo quà tặng mới thành công');
      }

      resetGiftModal();
      fetchGifts();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      console.error('Error saving gift:', error);
      antdMessage.error('Có lỗi xảy ra khi lưu quà tặng');
    } finally {
      setSubmitting(false);
    }
  }, [buildStoreAllocationsFromForm, editingGift, fetchGifts, giftForm, imageFile, resetGiftModal]);

  const handleToggleStatus = useCallback(async (gift: Gift, nextIsActive: boolean) => {
    setToggleLoadingId(gift.id);
    try {
      await giftService.toggleGiftStatus(gift.id, nextIsActive);
      antdMessage.success(nextIsActive ? `Đã hiển thị lại quà "${gift.name}"` : `Đã tạm ẩn quà "${gift.name}"`);
      await fetchGifts();
    } catch (error) {
      console.error('Error toggling gift status:', error);
      antdMessage.error('Không thể thay đổi trạng thái quà tặng');
    } finally {
      if (isMountedRef.current) {
        setToggleLoadingId(null);
      }
    }
  }, [fetchGifts]);

  const handleResetGiftNow = useCallback(async (gift: Gift) => {
    setResettingGiftId(gift.id);
    try {
      const result = await giftService.resetGiftNow(gift.id);
      if (result.applied) {
        const resetAtText = result.resetAt ? ` lúc ${dayjs(result.resetAt).format('HH:mm DD/MM/YYYY')}` : '';
        antdMessage.success(`Đã reset quà "${gift.name}"${resetAtText}. Khách có thể nhận lại từ bây giờ.`);
      } else {
        antdMessage.info(`Quà "${gift.name}" đã ở trạng thái mới nhất, không cần reset.`);
      }
      await fetchGifts();
    } catch (error) {
      console.error('Error resetting gift:', error);
      antdMessage.error('Không thể reset quà tặng');
    } finally {
      if (isMountedRef.current) {
        setResettingGiftId(null);
      }
    }
  }, [fetchGifts]);

  const handleDeleteGift = useCallback(async (gift: Gift) => {
    setDeletingGiftId(gift.id);
    try {
      await giftService.deleteGift(gift.id);
      antdMessage.success(`Đã xoá quà "${gift.name}"`);
      await fetchGifts();
    } catch (error) {
      console.error('Error deleting gift:', error);
      antdMessage.error('Không thể xoá quà tặng');
    } finally {
      if (isMountedRef.current) {
        setDeletingGiftId(null);
      }
    }
  }, [fetchGifts]);

  const handleResetAllGifts = useCallback(async () => {
    setResettingAll(true);
    try {
      const result = await giftService.resetAllGifts();
      const skipNote = result.skipped > 0 ? ` (${result.skipped} quà đã ở trạng thái mới)` : '';

      if (result.failed > 0) {
        antdMessage.warning(`Đã reset ${result.applied}/${result.processed} quà${skipNote}. ${result.failed} quà lỗi, vui lòng kiểm tra console.`);
        const errorEntries = result.results.filter((entry) => entry.error || entry.reason === 'error');
        if (errorEntries.length > 0) {
          console.error('Reset failures:', errorEntries);
        }
      } else if (result.applied === 0 && result.processed > 0) {
        antdMessage.info(`Tất cả ${result.processed} quà đã ở trạng thái mới nhất, không cần reset.`);
      } else {
        antdMessage.success(`Đã reset ${result.applied}/${result.processed} quà tặng${skipNote}`);
      }
      await fetchGifts();
    } catch (error) {
      console.error('Error resetting all gifts:', error);
      antdMessage.error('Không thể reset tất cả quà tặng');
    } finally {
      if (isMountedRef.current) {
        setResettingAll(false);
      }
    }
  }, [fetchGifts]);

  const handleOpenAssign = useCallback((gift: Gift) => {
    if (!isGiftActive(gift)) {
      antdMessage.warning('Quà đang tạm ẩn nên không thể gán mới');
      return;
    }

    const storeAllocations = gift.storeAllocations || [];

    if (storeAllocations.length === 0 || !storeAllocations.some((allocation) => allocation.totalQuantity > 0)) {
      antdMessage.warning('Quà này chưa được phân bổ cho cơ sở nào');
      return;
    }

    if (!storeAllocations.some((allocation) => allocation.availableQuantity > 0)) {
      antdMessage.warning('Tất cả cơ sở đã hết lượt đăng ký quà');
      return;
    }

    setAssigningGift(gift);
    setCurrentAssignment(null);
    assignForm.resetFields();
    assignForm.setFieldsValue({
      giftId: gift.id,
    });

    loadUsers().catch(() => undefined);
    setAssignModalVisible(true);
  }, [assignForm, loadUsers]);

  const buildAssignPayload = useCallback((values: AssignFormValues): AssignGiftPayload => {
    const matchedStore = assignableStoreAllocations.find((allocation) => allocation.storeId === values.storeId);
    return {
      giftId: values.giftId,
      userId: values.userId,
      storeId: values.storeId,
      storeName: matchedStore?.storeName,
    };
  }, [assignableStoreAllocations]);

  const handleAssignSubmit = useCallback(async () => {
    try {
      const values = await assignForm.validateFields();
      const selectedGift = gifts.find((gift) => gift.id === values.giftId);
      const selectedAllocation = selectedGift?.storeAllocations?.find((allocation) => allocation.storeId === values.storeId);

      if (!selectedGift || !isGiftActive(selectedGift)) {
        antdMessage.warning('Quà đang tạm ẩn nên không thể gán mới');
        return;
      }

      if (!selectedAllocation || selectedAllocation.totalQuantity <= 0) {
        antdMessage.warning('Cơ sở này chưa được phân bổ quà');
        return;
      }

      if (selectedAllocation.availableQuantity <= 0) {
        antdMessage.warning('Cơ sở này đã hết lượt đăng ký quà');
        return;
      }

      setAssignSubmitting(true);
      const assignment = await giftService.assignGift(buildAssignPayload(values));
      antdMessage.success('Gán quà tặng thành công');
      setCurrentAssignment(assignment);
      fetchGifts();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      console.error('Error assigning gift:', error);
      antdMessage.error('Không thể gán quà tặng');
    } finally {
      setAssignSubmitting(false);
    }
  }, [assignForm, buildAssignPayload, fetchGifts, gifts]);

  const handleRefreshQR = useCallback(async () => {
    if (!currentAssignment) return;
    try {
      const refreshed = await giftService.getAssignmentQR(currentAssignment.assignmentId);
      setCurrentAssignment({
        ...currentAssignment,
        status: refreshed.status,
        qrCode: refreshed.qrCode,
        qrTarget: refreshed.qrTarget,
        storeId: refreshed.storeId || currentAssignment.storeId,
        storeName: refreshed.storeName || currentAssignment.storeName,
      });
      antdMessage.success('Đã cập nhật QR');
    } catch (error) {
      console.error('Error refreshing QR:', error);
      antdMessage.error('Không thể tải lại QR');
    }
  }, [currentAssignment]);

  const openAssignedModal = useCallback(async (gift: Gift) => {
    const requestId = assignedModalRequestIdRef.current + 1;
    assignedModalRequestIdRef.current = requestId;
    setAssignedGift(gift);
    setAssignedRows([]);
    setAssignedLoading(true);

    try {
      const detail = await giftService.getGiftDetail(gift.id);
      const assignmentDocs = await giftService.getGiftAssignments(gift.id);
      const rowMap = new Map<string, {
        assignmentId: string;
        userId: string;
        userName?: string;
        assignedAt?: string;
        status?: string;
        storeName?: string;
        source?: string;
      }>();

      assignmentDocs.forEach((assignment) => {
        if (!assignment.assignmentId) return;

        rowMap.set(assignment.assignmentId, {
          assignmentId: assignment.assignmentId,
          userId: assignment.userId || assignment.userInfo?.id || '',
          userName: assignment.userInfo?.name,
          assignedAt: assignment.assignedAt,
          status: assignment.status,
          storeName: assignment.storeName || assignment.metadata?.storeName,
          source: assignment.metadata?.source,
        });
      });

      (detail.assignedUsers || []).forEach((user: any) => {
        if (!user.assignmentId || rowMap.has(user.assignmentId)) return;

        rowMap.set(user.assignmentId, {
          assignmentId: user.assignmentId,
          userId: user.userId,
          userName: user.name,
          assignedAt: user.assignedAt,
          status: 'assigned',
          storeName: user.storeName,
          source: 'gift.assignedUsers',
        });
      });

      const baseRows = Array.from(rowMap.values());

      const uniqueUserIds = Array.from(new Set(baseRows.map((row) => row.userId).filter(Boolean)));
      const idToName = new Map<string, string>();

      users.forEach((user) => {
        if (user.id) {
          const userName = user.name || '';
          idToName.set(user.id, userName);
          userNameCacheRef.current.set(user.id, userName);
        }
      });

      const missingUserIds = uniqueUserIds.filter((userId) => !idToName.has(userId));
      if (missingUserIds.length > 0) {
        const missingNames = await Promise.all(
          missingUserIds.map(async (userId) => ({
            userId,
            userName: await getUserNameById(userId),
          })),
        );

        missingNames.forEach(({ userId, userName }) => {
          if (userName !== undefined) {
            idToName.set(userId, userName);
          }
        });
      }

      const rows = baseRows.map((row) => ({
        ...row,
        userName: row.userName || idToName.get(row.userId),
      }));

      if (!isMountedRef.current || assignedModalRequestIdRef.current !== requestId) {
        return;
      }

      setAssignedRows(rows);
      setAssignedModalVisible(true);
    } catch (error) {
      if (isMountedRef.current && assignedModalRequestIdRef.current === requestId) {
        console.error('Error fetching assigned users:', error);
        antdMessage.error('Không thể tải danh sách đã gán');
      }
    } finally {
      if (isMountedRef.current && assignedModalRequestIdRef.current === requestId) {
        setAssignedLoading(false);
      }
    }
  }, [getUserNameById, users]);

  const viewQRForAssignment = useCallback(async (assignmentId: string) => {
    try {
      const data = await giftService.getAssignmentQR(assignmentId);
      setQrPreviewData({
        qrCode: data.qrCode,
        qrTarget: data.qrTarget,
        status: data.status,
        storeName: data.storeName,
      });
      setQrPreviewVisible(true);
    } catch (error) {
      console.error('Error fetching assignment QR:', error);
      antdMessage.error('Không thể tải QR');
    }
  }, []);

  const handleDeleteAssignedGift = useCallback(async (row: { assignmentId: string; userId: string; userName?: string }) => {
    if (!assignedGift) {
      antdMessage.error('Không xác định được quà cần xóa lượt gán');
      return;
    }

    setDeletingAssignmentId(row.assignmentId);
    try {
      await giftService.deleteGiftAssignment(assignedGift.id, row.assignmentId);
      setAssignedRows((prev) => prev.filter((item) => item.assignmentId !== row.assignmentId));
      await fetchGifts();
      antdMessage.success(`Đã xóa lượt gán của ${row.userName || row.userId}`);
    } catch (error) {
      console.error('Error deleting gift assignment:', error);
      antdMessage.error('Không thể xóa lượt gán quà');
    } finally {
      if (isMountedRef.current) {
        setDeletingAssignmentId(null);
      }
    }
  }, [assignedGift, fetchGifts]);

  const handleImageChange = ({ fileList: nextFileList }: { fileList: UploadFile[] }) => {
    setFileList(nextFileList);
    if (nextFileList.length > 0) {
      const file = nextFileList[0].originFileObj || nextFileList[0];
      setImageFile(file instanceof File ? file : null);
      return;
    }
    setImageFile(null);
  };

  const handleImageRemove = () => {
    setImageFile(null);
    setFileList([]);
    return true;
  };

  const columns: ColumnsType<Gift> = useMemo(() => [
    {
      title: 'Ảnh',
      key: 'image',
      width: 100,
      render: (_: unknown, record: Gift) => {
        if (record.imageUrl) {
          return (
            <img
              src={record.imageUrl}
              alt={record.name}
              style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          );
        }
        return <Text type="secondary">-</Text>;
      },
    },
    {
      title: 'Tên quà',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Gift) => (
        <Space direction="vertical" size={0}>
          <Space size={8} wrap>
            <Text strong>{text}</Text>
            {!isGiftActive(record) && <Tag color="default">Tạm ẩn</Tag>}
            {record.resetConfig?.enabled && (
              <Tag color="blue">
                Reset {String(record.resetConfig.dailyResetHour).padStart(2, '0')}:
                {String(record.resetConfig.dailyResetMinute).padStart(2, '0')}
              </Tag>
            )}
            {record.resetConfig?.enabled && record.resetConfig.lastResetAt && (
              <Tag color="geekblue">
                Reset gần nhất: {formatVietnameseDateTime(record.resetConfig.lastResetAt)}
              </Tag>
            )}
          </Space>
          {record.description && <Text type="secondary">{record.description}</Text>}
          {(!record.storeAllocations || record.storeAllocations.length === 0) && (
            <Tag color="warning">Chưa phân bổ cơ sở</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 140,
      render: (_: boolean | undefined, record: Gift) => (
        <Switch
          checked={isGiftActive(record)}
          loading={toggleLoadingId === record.id}
          onChange={(checked) => handleToggleStatus(record, checked)}
          checkedChildren="Hoạt động"
          unCheckedChildren="Tạm ẩn"
          size="small"
        />
      ),
    },
    {
      title: 'Tổng số lượng',
      dataIndex: 'totalQuantity',
      key: 'totalQuantity',
      align: 'right',
    },
    {
      title: 'Đang còn',
      dataIndex: 'availableQuantity',
      key: 'availableQuantity',
      align: 'right',
      render: (value: number) => <Tag color={value > 0 ? 'green' : 'red'}>{value}</Tag>,
    },
    {
      title: 'Đã gán',
      dataIndex: 'assignedCount',
      key: 'assignedCount',
      align: 'right',
    },
    {
      title: 'Đã sử dụng',
      dataIndex: 'usedQuantity',
      key: 'usedQuantity',
      align: 'right',
    },
    {
      title: 'Thao tác',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<QrcodeOutlined />}
            disabled={!isGiftActive(record)}
            onClick={() => handleOpenAssign(record)}
          />
          <Button type="text" icon={<TeamOutlined />} onClick={() => openAssignedModal(record)} />
          <Button type="text" icon={<EditOutlined />} onClick={() => openEditModal(record)} />
          <Popconfirm
            title="Reset quà ngay?"
            description="Số tồn của tất cả cơ sở sẽ về mức tối đa, các lượt gán hiện tại sẽ bị xoá (lượt đã sử dụng được lưu vào lịch sử)."
            okText="Reset"
            cancelText="Huỷ"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleResetGiftNow(record)}
          >
            <Button
              type="text"
              icon={<SyncOutlined />}
              loading={resettingGiftId === record.id}
              title="Reset quà ngay"
            />
          </Popconfirm>
          <Popconfirm
            title="Xoá quà tặng?"
            description="Quà sẽ bị xoá vĩnh viễn khỏi hệ thống. Hành động này không thể hoàn tác."
            okText="Xoá"
            cancelText="Huỷ"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDeleteGift(record)}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              loading={deletingGiftId === record.id}
              title="Xoá quà"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ], [
    deletingGiftId,
    handleDeleteGift,
    handleOpenAssign,
    handleResetGiftNow,
    handleToggleStatus,
    openAssignedModal,
    openEditModal,
    resettingGiftId,
    toggleLoadingId,
  ]);

  return (
    <div>
      <div className="admin-page-header">
        <div className="admin-page-title">
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
            <GiftOutlined />
            Quản lý quà tặng
          </h1>
          <Space wrap>
            <Tag color="blue">{giftStats.total} quà</Tag>
            <Tag color="green">{giftStats.active} đang hoạt động</Tag>
            <Tag color="default">{giftStats.inactive} tạm ẩn</Tag>
          </Space>
        </div>

        <div className="admin-page-actions">
          <div className="admin-search-filters">
            <Space>
              <Select<GiftStatusFilter>
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: 180 }}
                options={[
                  { value: 'all', label: 'Tất cả quà' },
                  { value: 'active', label: 'Đang hoạt động' },
                  { value: 'inactive', label: 'Đã tạm ẩn' },
                ]}
              />
              <Button icon={<ReloadOutlined />} onClick={fetchGifts} loading={loading} className="admin-button">
                Tải lại
              </Button>
              <Popconfirm
                title="Reset tất cả quà ngay?"
                description="Tồn của mọi quà sẽ về mức tối đa, các lượt gán hiện tại sẽ bị xoá (lượt đã sử dụng được lưu vào lịch sử). Hành động này không thể hoàn tác."
                okText="Reset tất cả"
                cancelText="Huỷ"
                okButtonProps={{ danger: true }}
                onConfirm={handleResetAllGifts}
              >
                <Button
                  icon={<SyncOutlined />}
                  loading={resettingAll}
                  className="admin-button"
                  danger
                >
                  Reset tất cả
                </Button>
              </Popconfirm>
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal} className="admin-button">
                Thêm quà tặng
              </Button>
            </Space>
          </div>
        </div>
      </div>

      <div className="admin-form-section">
        <div className="admin-table-container">
          <Table<Gift>
            rowKey="id"
            loading={loading}
            dataSource={filteredGifts}
            columns={columns}
            expandable={{
              expandedRowRender: (record) => {
                if (!record.storeAllocations || record.storeAllocations.length === 0) {
                  return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Quà chưa được phân bổ cơ sở" />;
                }

                return (
                  <Table<GiftStoreAllocation>
                    rowKey="storeId"
                    dataSource={record.storeAllocations}
                    pagination={false}
                    size="small"
                    columns={[
                      { title: 'Cơ sở', dataIndex: 'storeName', key: 'storeName' },
                      { title: 'Tổng', dataIndex: 'totalQuantity', key: 'totalQuantity', align: 'right' },
                      {
                        title: 'Còn đăng ký',
                        dataIndex: 'availableQuantity',
                        key: 'availableQuantity',
                        align: 'right',
                        render: (value: number) => <Tag color={value > 0 ? 'green' : 'red'}>{value}</Tag>,
                      },
                      { title: 'Đã đăng ký', dataIndex: 'assignedCount', key: 'assignedCount', align: 'right' },
                      { title: 'Đã sử dụng', dataIndex: 'usedQuantity', key: 'usedQuantity', align: 'right' },
                    ]}
                  />
                );
              },
            }}
            pagination={{ pageSize: 10, position: ['bottomRight'] }}
          />
        </div>
      </div>

      <Modal
        open={assignedModalVisible}
        title="Danh sách đã gán"
        onCancel={() => {
          setAssignedModalVisible(false);
          setAssignedGift(null);
        }}
        footer={null}
        width={1100}
        destroyOnClose
      >
        <Table
          rowKey="assignmentId"
          loading={assignedLoading}
          dataSource={assignedRows}
          size="small"
          scroll={{ x: 1120 }}
          pagination={{ pageSize: 10, position: ['bottomRight'] }}
          columns={[
            {
              title: 'Mã người dùng',
              dataIndex: 'userId',
              key: 'userId',
              width: 220,
              ellipsis: true,
            },
            {
              title: 'Tên người dùng',
              dataIndex: 'userName',
              key: 'userName',
              width: 180,
              ellipsis: true,
            },
            {
              title: 'Cơ sở',
              dataIndex: 'storeName',
              key: 'storeName',
              width: 150,
              ellipsis: true,
              render: (value: string | undefined) => value || '-',
            },
            {
              title: 'Thời gian gán',
              dataIndex: 'assignedAt',
              key: 'assignedAt',
              width: 180,
              render: (value: string | undefined) => formatDateTime(value),
            },
            {
              title: 'Trạng thái',
              dataIndex: 'status',
              key: 'status',
              width: 140,
              render: (value: string | undefined) => (
                <Tag color={value === 'redeemed' ? 'green' : value === 'expired' ? 'red' : 'blue'}>
                  {value === 'redeemed' ? 'Đã sử dụng' : value === 'expired' ? 'Hết hạn' : 'Đã đăng ký'}
                </Tag>
              ),
            },
            {
              title: 'Nguồn',
              dataIndex: 'source',
              key: 'source',
              width: 120,
              ellipsis: true,
              render: (value: string | undefined) => value || '-',
            },
            {
              title: 'Thao tác',
              key: 'action',
              width: 220,
              render: (_: unknown, row: any) => (
                <Space size={4} wrap>
                  <Button type="link" icon={<EyeOutlined />} onClick={() => viewQRForAssignment(row.assignmentId)}>
                    Xem QR
                  </Button>
                  <Popconfirm
                    title="Xóa lượt gán quà?"
                    description="Chỉ nên dùng để test. Thao tác này sẽ trả lại lượt quà trong admin."
                    okText="Xóa"
                    cancelText="Hủy"
                    okButtonProps={{ danger: true, loading: deletingAssignmentId === row.assignmentId }}
                    onConfirm={() => handleDeleteAssignedGift(row)}
                  >
                    <Button type="link" danger icon={<DeleteOutlined />} loading={deletingAssignmentId === row.assignmentId}>
                      Xóa
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Modal>

      <Modal
        open={qrPreviewVisible}
        title="QR gán quà"
        onCancel={() => setQrPreviewVisible(false)}
        footer={null}
        destroyOnClose
      >
        {qrPreviewData?.qrCode && (
          <div style={{ textAlign: 'center' }}>
            <img src={qrPreviewData.qrCode} alt="Gift QR" style={{ width: 220, height: 220 }} />
            <div style={{ marginTop: 8 }}>
              <Typography.Paragraph copyable>{qrPreviewData.qrTarget}</Typography.Paragraph>
            </div>
            {qrPreviewData?.status && (
              <Tag color={qrPreviewData.status === 'assigned' ? 'blue' : 'green'}>
                {qrPreviewData.status === 'assigned' ? 'Đang chờ sử dụng' : 'Đã sử dụng'}
              </Tag>
            )}
            {qrPreviewData?.storeName && (
              <div style={{ marginTop: 8 }}>
                <Tag color="orange">Cơ sở: {qrPreviewData.storeName}</Tag>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        open={giftModalVisible}
        title={editingGift ? 'Cập nhật quà tặng' : 'Thêm quà tặng mới'}
        onCancel={resetGiftModal}
        onOk={handleGiftSubmit}
        okText={editingGift ? 'Cập nhật' : 'Tạo mới'}
        cancelText="Hủy"
        confirmLoading={submitting}
        destroyOnClose
        width={720}
      >
        <Form form={giftForm} layout="vertical">
          <Form.Item
            name="name"
            label="Tên quà tặng"
            rules={[{ required: true, message: 'Vui lòng nhập tên quà tặng' }]}
          >
            <Input placeholder="Ví dụ: Free Latte" maxLength={120} />
          </Form.Item>

          <Form.Item name="description" label="Mô tả">
            <Input.TextArea placeholder="Mô tả chi tiết quà tặng" rows={3} maxLength={500} />
          </Form.Item>

          <Form.Item label="Tổng số lượng">
            <InputNumber value={computedTotalQuantity} style={{ width: '100%' }} disabled />
          </Form.Item>

          <div style={{ marginBottom: 16 }}>
            <Text strong>Phân bổ theo cơ sở</Text>
            <div style={{ marginTop: 8 }}>
              {!loadingStores && editableStores.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có cơ sở hoạt động để phân bổ" />
              ) : (
                <Space direction="vertical" style={{ width: '100%' }} size={12}>
                  {editableStores.map((store) => {
                    const existingAllocation = editingGift?.storeAllocations?.find((allocation) => allocation.storeId === store.id);
                    return (
                      <div
                        key={store.id}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'minmax(220px, 1fr) 180px',
                          gap: 16,
                          alignItems: 'center',
                          padding: '12px 0',
                          borderBottom: '1px solid #f0f0f0',
                        }}
                      >
                        <Space direction="vertical" size={2}>
                          <Text strong>{store.name}</Text>
                          {existingAllocation ? (
                            <Text type="secondary">
                              Còn đăng ký {existingAllocation.availableQuantity} / {existingAllocation.totalQuantity} | Đã đăng ký {existingAllocation.assignedCount} | Đã dùng {existingAllocation.usedQuantity}
                            </Text>
                          ) : (
                            <Text type="secondary">Chưa phân bổ cho cơ sở này</Text>
                          )}
                        </Space>
                        <Form.Item
                          name={['storeQuantities', store.id]}
                          label="Số lượng"
                          style={{ marginBottom: 0 }}
                          rules={[{ type: 'number', min: 0, message: 'Số lượng phải lớn hơn hoặc bằng 0' }]}
                        >
                          <InputNumber min={0} style={{ width: '100%' }} placeholder="Nhập số lượng" />
                        </Form.Item>
                      </div>
                    );
                  })}
                </Space>
              )}
            </div>
          </div>

          <div
            style={{
              marginBottom: 16,
              padding: 16,
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              background: '#fafafa',
            }}
          >
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
                <Space direction="vertical" size={2}>
                  <Text strong>Reset quà hằng ngày</Text>
                  <Text type="secondary">
                    Khi quá khung giờ này, tồn quà sẽ trở về mức tối đa theo từng cơ sở để khách có thể nhận lại.
                  </Text>
                </Space>
                <Form.Item name="resetEnabled" valuePropName="checked" style={{ marginBottom: 0 }}>
                  <Switch checkedChildren="Bật" unCheckedChildren="Tắt" />
                </Form.Item>
              </Space>

              <Form.Item
                name="resetTime"
                label="Giờ reset"
                style={{ marginBottom: 0 }}
                rules={[
                  {
                    validator: async (_, value) => {
                      if (!giftForm.getFieldValue('resetEnabled') || value) {
                        return;
                      }
                      throw new Error('Vui lòng chọn giờ reset');
                    },
                  },
                ]}
              >
                <TimePicker
                  format="HH:mm"
                  minuteStep={1}
                  style={{ width: 180 }}
                  disabled={!watchedResetEnabled}
                  allowClear={false}
                />
              </Form.Item>
            </Space>
          </div>

          <Form.Item label="Ảnh minh họa" extra="Hỗ trợ định dạng: JPG, PNG. Tối đa 5MB.">
            <Upload
              listType="picture-card"
              fileList={fileList}
              onChange={handleImageChange}
              onRemove={handleImageRemove}
              beforeUpload={(file) => {
                const isImage = file.type.startsWith('image/');
                const isLt5M = file.size / 1024 / 1024 < 5;

                if (!isImage) {
                  antdMessage.error('Chỉ cho phép upload file hình ảnh (JPG, PNG)!');
                  return false;
                }

                if (!isLt5M) {
                  antdMessage.error(`Kích thước hình ảnh (${(file.size / 1024 / 1024).toFixed(2)}MB) vượt quá giới hạn 5MB!`);
                  return false;
                }

                return false;
              }}
              accept="image/jpeg,image/png,image/jpg"
              maxCount={1}
            >
              {fileList.length >= 1 ? null : (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Tải lên</div>
                </div>
              )}
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={assignModalVisible}
        title={assigningGift ? `Gán quà: ${assigningGift.name}` : 'Gán quà tặng'}
        onCancel={() => {
          setAssignModalVisible(false);
          setAssigningGift(null);
          assignForm.resetFields();
          setCurrentAssignment(null);
        }}
        onOk={handleAssignSubmit}
        okText="Gán quà"
        cancelText="Hủy"
        confirmLoading={assignSubmitting}
        destroyOnClose
        width={600}
      >
        <Form form={assignForm} layout="vertical">
          <Form.Item name="giftId" hidden rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Form.Item
            name="storeId"
            label="Cơ sở"
            rules={[{ required: true, message: 'Vui lòng chọn cơ sở' }]}
          >
            <Select
              placeholder="Chọn cơ sở để gán quà"
              options={assignableStoreOptions}
              notFoundContent={assigningGift ? 'Quà chưa được phân bổ cơ sở' : undefined}
            />
          </Form.Item>

          <Form.Item
            name="userId"
            label="ID khách hàng"
            rules={[{ required: true, message: 'Vui lòng chọn hoặc nhập ID khách hàng' }]}
          >
            <AutoComplete
              placeholder="Nhập ID hoặc chọn từ danh sách"
              onSelect={(value) => assignForm.setFieldsValue({ userId: value as string })}
              onSearch={() => undefined}
              options={userOptions}
              filterOption={(inputValue, option) => (
                (option?.label as string)?.toLowerCase().includes(inputValue.toLowerCase())
                || (option?.value as string)?.toLowerCase().includes(inputValue.toLowerCase())
              )}
              notFoundContent={loadingUsers ? <Spin size="small" /> : undefined}
              allowClear
            />
          </Form.Item>
        </Form>

        {assigningGift && assignableStoreAllocations.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Text strong>Tồn theo cơ sở</Text>
            <div style={{ marginTop: 8 }}>
              <Table<GiftStoreAllocation>
                rowKey="storeId"
                dataSource={assignableStoreAllocations}
                size="small"
                pagination={false}
                columns={[
                  { title: 'Cơ sở', dataIndex: 'storeName', key: 'storeName' },
                  { title: 'Tổng', dataIndex: 'totalQuantity', key: 'totalQuantity', align: 'right' },
                  { title: 'Còn đăng ký', dataIndex: 'availableQuantity', key: 'availableQuantity', align: 'right' },
                ]}
              />
            </div>
          </div>
        )}

        {currentAssignment && (
          <div style={{ marginTop: 24 }}>
            <Descriptions bordered size="small" column={1} title="Thông tin gán quà">
              <Descriptions.Item label="Trạng thái">
                <Tag color={currentAssignment.status === 'assigned' ? 'blue' : 'green'}>
                  {currentAssignment.status === 'assigned' ? 'Đang chờ sử dụng' : 'Đã sử dụng'}
                </Tag>
              </Descriptions.Item>
              {currentAssignment.storeName && (
                <Descriptions.Item label="Cơ sở">
                  {currentAssignment.storeName}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="QR Link">
                <Text copyable>{currentAssignment.qrTarget}</Text>
              </Descriptions.Item>
            </Descriptions>

            {currentAssignment.qrCode && (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <img src={currentAssignment.qrCode} alt="Gift QR" style={{ width: 200, height: 200 }} />
                <div style={{ marginTop: 8 }}>
                  <Button icon={<ReloadOutlined />} onClick={handleRefreshQR}>
                    Làm mới QR
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default GiftManagementSection;
